// Job 52: hand-correct the Expected Invoice.
//
// Deliberately shaped like ParsedInvoiceEditor (the retailer-invoice side) so the
// two halves of a comparison are edited the same way. The difference is what an
// edit here means: the expected invoice is calculated, so overruling it is a
// judgement that needs a reason attached and a stamp that survives the next
// recalculation — see utils/shadowOverride.

import { useState } from 'react';
import { Plus, Trash2, Save, X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { recalcShadowTotals } from '../../utils/shadowOverride';
import { vocabularyNameFor } from '../../utils/expectedVocabulary';
import type { ShadowBillResult, ShadowBillLine, ShadowBillSection } from '../../types/billTypes';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

const EDITABLE_SECTIONS: { key: ShadowBillSection; label: string }[] = [
  { key: 'retail',        label: 'Retail Energy Charges' },
  { key: 'distribution',  label: 'Network / Distribution Charges' },
  { key: 'metering',      label: 'Metering Charges' },
  { key: 'environmental', label: 'Environmental Charges' },
  { key: 'other',         label: 'Other Charges' },
  { key: 'gst',           label: 'GST' },
];

type Props = {
  initialResult: ShadowBillResult;
  onSave: (result: ShadowBillResult, note: string) => Promise<void>;
  onCancel: () => void;
};

const numOrUndef = (raw: string): number | undefined =>
  raw === '' ? undefined : (Number.isFinite(parseFloat(raw)) ? parseFloat(raw) : undefined);

export function ExpectedInvoiceEditor({ initialResult, onSave, onCancel }: Props) {
  const [lines, setLines] = useState<ShadowBillLine[]>(() => [...initialResult.lines]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const computed = recalcShadowTotals(lines, initialResult);
  const movedBy = computed.total - initialResult.total;

  const updateLine = (idx: number, patch: Partial<ShadowBillLine>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const deleteLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const addLine = (section: ShadowBillSection) =>
    setLines((prev) => [...prev, { section, description: '', amount: 0 }]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(computed, note);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3 bg-white border-b border-amber-200">
        <div>
          <p className="text-sm font-semibold text-gray-900">Edit Expected Invoice</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Overrides the calculated values. Recalculating later will ask before replacing your edit.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : 'Save Override'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <X className="h-3.5 w-3.5 shrink-0" /> {error}
        </div>
      )}

      <div className="divide-y divide-gray-200">
        {EDITABLE_SECTIONS.map(({ key, label }) => {
          const sectionLines = lines.map((l, idx) => ({ l, idx })).filter(({ l }) => l.section === key);
          return (
            <div key={key} className="bg-white">
              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 tabular-nums">
                    Total: <span className="font-semibold text-gray-800">{fmt(computed.section_totals[key] ?? 0)}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    onClick={() => addLine(key)}
                  >
                    <Plus className="h-3 w-3" /> Add Line
                  </Button>
                </div>
              </div>

              {sectionLines.length > 0 ? (
                <div className="px-4 py-2 space-y-1.5">
                  {sectionLines.map(({ l, idx }) => {
                    // The vocabulary name this line will be shown under, when
                    // the description can be placed in the charge map's
                    // vocabulary.
                    //
                    // Reported BESIDE the field, never substituted into it: this
                    // input is the stored `shadow_result` description, and
                    // writing a display name into it would rename the
                    // calculation's own output on save — which is what every
                    // already-stored bill pairs on and what saved exclusions are
                    // keyed by.
                    const vocabName = vocabularyNameFor(l.description);
                    return (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={l.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        placeholder="Description"
                        className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {vocabName && vocabName !== l.description && (
                        <span
                          className="shrink-0 text-[10px] text-gray-400 italic"
                          title="The charge map's name for this charge. The expected invoice and the comparison both show it under this name; the description you type is what is stored."
                        >
                          shows as "{vocabName}"
                        </span>
                      )}
                      {/* Quantity and rate are kept editable as the record of how the
                          amount was reached. They are not multiplied into it: rate
                          units differ per line (c/kWh vs dollars) and guessing would
                          produce confidently wrong money. */}
                      <input
                        type="number"
                        step="any"
                        value={l.volume ?? ''}
                        onChange={(e) => updateLine(idx, { volume: numOrUndef(e.target.value) })}
                        placeholder="Qty"
                        title="Quantity"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={l.volume_unit ?? ''}
                        onChange={(e) => updateLine(idx, { volume_unit: e.target.value || undefined })}
                        placeholder="Unit"
                        title="QTY unit — meters, servicePoint, kVA"
                        className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {/* Usage is separate from QTY, not a second name for it: a
                          metering line is a meter count (QTY) across a number of
                          days (Usage), and both have to be correctable. */}
                      <input
                        type="number"
                        step="any"
                        value={l.usage ?? ''}
                        onChange={(e) => updateLine(idx, { usage: numOrUndef(e.target.value) ?? null })}
                        placeholder="Usage"
                        title="Usage — kWh consumed, or days the charge ran over"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={l.usage_unit ?? ''}
                        onChange={(e) => updateLine(idx, { usage_unit: e.target.value || null })}
                        placeholder="Unit"
                        title="Usage unit — kWh or days"
                        className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        type="number"
                        step="any"
                        value={l.rate ?? ''}
                        onChange={(e) => updateLine(idx, { rate: numOrUndef(e.target.value) })}
                        placeholder="Rate"
                        title="Rate"
                        className="w-24 px-2 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="relative shrink-0">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={l.amount ?? 0}
                          onChange={(e) => updateLine(idx, { amount: parseFloat(e.target.value) || 0 })}
                          title="Amount"
                          className={cn(
                            'w-28 pl-6 pr-2.5 py-1.5 text-sm text-right border border-gray-200 rounded-md bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500',
                            (l.amount ?? 0) < 0 && 'text-green-700',
                          )}
                        />
                      </div>
                      <button
                        onClick={() => deleteLine(idx)}
                        title="Remove line"
                        className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="px-4 py-2 text-xs text-gray-400 italic">No lines — click Add Line to add one</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 space-y-3">
        <div className="max-w-sm ml-auto space-y-1.5">
          <div className="flex justify-between text-sm pt-1.5">
            <span className="text-gray-600">Subtotal (excl. GST)</span>
            <span className="tabular-nums font-medium">{fmt(computed.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">GST</span>
            <span className="tabular-nums text-gray-600">{fmt(computed.gst)}</span>
          </div>
          <div className="flex justify-between text-base pt-1.5 border-t border-gray-300">
            <span className="font-semibold text-gray-800">Expected total</span>
            <span className="tabular-nums font-bold text-gray-900">{fmt(computed.total)}</span>
          </div>
          {Math.abs(movedBy) >= 0.01 && (
            <p className={cn('flex items-center justify-end gap-1.5 text-xs',
              movedBy > 0 ? 'text-amber-700' : 'text-blue-700')}>
              <AlertTriangle className="h-3.5 w-3.5" />
              {movedBy > 0 ? '+' : ''}{fmt(movedBy)} versus the calculated {fmt(initialResult.total)}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="override-note" className="text-xs font-medium text-gray-600">
            Reason for the override
          </label>
          <input
            id="override-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. metering charge missing from the tariff configuration"
            className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            Recorded in the site's history alongside the old and new totals.
          </p>
        </div>
      </div>
    </div>
  );
}
